import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus } from 'lucide-react';
import { LootItem, ConsumableItem, isConsumableItem } from '../../../types/equipment';
import { CharacterData } from '../../../types/character';
import { PropertyRow } from './PropertyRow';
import { OfficialBadge } from './OfficialBadge';
import { EffectChip } from './EffectChip';
import { ModifierBreakdown } from './ModifierBreakdown';
import { MAX_CONSUMABLE_HOLD } from '../../../constants/equipment';
import styles from './ItemCard.module.css';

interface ItemCardProps {
  item: LootItem | ConsumableItem;
  quantity?: number;
  equippedCount?: number;
  isExpanded?: boolean;
  onExpandToggle?: () => void;
  onQuantityChange?: (newQuantity: number) => void;
  onConsume?: () => void;
  onRemove?: () => void;
  onEquip?: () => void;
  onUnequip?: () => void;
  character?: CharacterData;
  onAddToCharacter?: (itemId: string) => Promise<void>;
  variant?: 'inventory' | 'library';
}

/**
 * ItemCard - Reusable card component for displaying items
 * Supports both inventory view (with quantity controls) and library view (with add button)
 * Features expand/collapse animation for detailed information
 */
export const ItemCard: React.FC<ItemCardProps> = ({
  item,
  quantity = 1,
  equippedCount = 0,
  isExpanded: controlledExpanded,
  onExpandToggle,
  onQuantityChange,
  onConsume,
  onRemove,
  onEquip,
  onUnequip,
  character,
  onAddToCharacter,
  variant = 'inventory'
}) => {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Use controlled or uncontrolled expanded state
  const isExpanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;
  const toggleExpanded = onExpandToggle || (() => setInternalExpanded(!internalExpanded));

  const isConsumable = isConsumableItem(item);
  const exceedsOfficialLimit = isConsumable && quantity > MAX_CONSUMABLE_HOLD;

  const handleQuantityChange = (delta: number) => {
    if (!onQuantityChange) return;

    const newQuantity = quantity + delta;

    // Validate quantity (minimum only - no max enforcement)
    if (newQuantity < 1) return;

    onQuantityChange(newQuantity);
  };

  const handleConsume = () => {
    if (onConsume && quantity > 0) {
      onConsume();
    }
  };

  const handleAdd = async () => {
    if (!onAddToCharacter) return;

    setIsLoading(true);
    try {
      await onAddToCharacter(item.id);
    } catch (error) {
      console.error('Failed to add item:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`${styles.itemCard} ${isExpanded ? styles.expanded : ''}`}>
      {/* Collapsed Header - Always Visible */}
      <button
        className={styles.header}
        onClick={toggleExpanded}
        aria-expanded={isExpanded}
        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${item.name} details`}
      >
        <span className={styles.expandIcon} aria-hidden="true">
          {isExpanded ? '▼' : '▶'}
        </span>
        <span className={styles.itemName}>{item.name}</span>

        {variant === 'inventory' && quantity > 0 && (
          <span className={styles.quantity} aria-label={`Quantity: ${quantity}`}>
            ×{quantity}
            {exceedsOfficialLimit && (
              <span
                className={styles.infoIcon}
                title="Official rules allow a maximum of 5 of each consumable"
                aria-label="Exceeds official limit"
              >
                ℹ️
              </span>
            )}
          </span>
        )}

        <OfficialBadge
          official={item.metadata.official}
          metadata={item.metadata}
          variant="inline"
        />
      </button>

      {/* Add Button - Outside header to avoid nested buttons */}
      {variant === 'library' && onAddToCharacter && (
        <button
          className={`${styles.actionButton} ${styles.addButtonHeader}`}
          onClick={(e) => {
            e.stopPropagation();
            handleAdd();
          }}
          disabled={isLoading}
          aria-label="Add to character"
        >
          {isLoading ? '...' : <Plus size={16} strokeWidth={3} />}
        </button>
      )}

      {/* Expanded Details - Animated */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            className={styles.details}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
          >
            <div className={styles.detailsContent}>
              {/* Basic Properties */}
              <div className={styles.properties}>
                {item.tier && (
                  <PropertyRow
                    label="Tier"
                    value={item.tier}
                  />
                )}
                {item.rarity && (
                  <PropertyRow
                    label="Rarity"
                    value={item.rarity}
                  />
                )}
              </div>

              {/* Feature */}
              {item.description && (
                <div className={styles.feature}>
                  <h4 className={styles.sectionTitle}>Feature</h4>
                  <p>{item.description}</p>
                </div>
              )}

              {/* Stat Modifiers */}
              {item.modifiers && item.modifiers.length > 0 && (
                <ModifierBreakdown
                  modifiers={item.modifiers}
                  character={character}
                  tier={character?.tier}
                />
              )}

              {/* Action Buttons */}
              {variant === 'inventory' && (
                <div className={styles.actions}>
                  <>
                    {/* Quantity Controls */}
                    {onQuantityChange && (
                      <div className={styles.quantityControl}>
                        <button
                          className={styles.quantityButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuantityChange(-1);
                          }}
                          disabled={quantity <= 1}
                          aria-label="Decrease quantity"
                        >
                          −
                        </button>
                        <span className={styles.quantityDisplay}>
                          {quantity}
                          {exceedsOfficialLimit && (
                            <span
                              className={styles.infoIconInline}
                              title="Official rules allow a maximum of 5 of each consumable"
                            >
                              ℹ️
                            </span>
                          )}
                        </span>
                        <button
                          className={styles.quantityButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuantityChange(1);
                          }}
                          aria-label="Increase quantity"
                        >
                          +
                        </button>
                      </div>
                    )}

                    {/* Consume Button (consumables only) */}
                    {isConsumable && onConsume && (
                      <button
                        className={`${styles.actionButton} ${styles.consumeButton}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleConsume();
                        }}
                        disabled={quantity <= 0}
                        aria-label="Consume item"
                      >
                        Consume (-1)
                      </button>
                    )}

                    {/* Equip/Unequip Buttons (loot only) */}
                    {!isConsumable && onEquip && onUnequip && (
                      <div className={styles.equipControl}>
                        <button
                          className={`${styles.actionButton} ${styles.unequipButton}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onUnequip();
                          }}
                          disabled={equippedCount <= 0}
                          aria-label="Unequip item"
                        >
                          Unequip
                        </button>
                        <span className={styles.equippedDisplay}>
                          {equippedCount}/{quantity} equipped
                        </span>
                        <button
                          className={`${styles.actionButton} ${styles.equipButton}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEquip();
                          }}
                          disabled={equippedCount >= quantity}
                          aria-label="Equip item"
                          title={equippedCount >= quantity ? 'All copies already equipped' : undefined}
                        >
                          Equip
                        </button>
                      </div>
                    )}

                    {/* Remove Button */}
                    {onRemove && (
                      <button
                        className={`${styles.actionButton} ${styles.removeButton}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemove();
                        }}
                        aria-label="Remove from inventory"
                      >
                        Remove
                      </button>
                    )}
                  </>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
